import React from 'react';
import { cn } from '../utils/cn';

export type IconButtonVariant = 'plain' | 'tonal' | 'filled' | 'outlined';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** Required for accessibility on icon-only buttons */
  'aria-label': string;
}

const variantStyles: Record<IconButtonVariant, string> = {
  plain:
    'bg-transparent text-surface-foreground hover:bg-surface-variant active:scale-[0.95]',
  tonal:
    'bg-primary-container text-primary-container-foreground hover:brightness-105 active:scale-[0.95]',
  filled:
    'bg-primary text-primary-foreground hover:bg-primary-hover active:scale-[0.95] shadow-level-1',
  outlined:
    'border border-outline bg-transparent text-foreground hover:bg-surface-variant active:scale-[0.95]',
};

const sizeStyles: Record<IconButtonSize, string> = {
  sm: 'h-8 w-8 [&_svg]:h-4 [&_svg]:w-4',
  md: 'h-10 w-10 [&_svg]:h-5 [&_svg]:w-5',
  lg: 'h-12 w-12 [&_svg]:h-6 [&_svg]:w-6',
};

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ variant = 'plain', size = 'md', className, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center rounded-full transition-all duration-2 ease-standard',
        'focus-visible:outline-none focus-visible:shadow-focus-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      {...props}
    />
  ),
);

IconButton.displayName = 'IconButton';
